import {
  Badge,
  confirm,
  DataTable,
  type TanstackReactTable,
  toast,
} from "@devhop/ui";
import { formatDateLocal } from "@/shared/lib/date-time";
import { EditIcon, EyeIcon, HistoryIcon, XIcon } from "lucide-react";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type { PositionWithJDListResult } from "@/modules/jd-mapping/domain/types";
import { RowActions } from "@/shared/ui/RowActions";
import { useCancelMapping } from "../api/queries";

type PositionRow = PositionWithJDListResult["data"][number];

type JDMappingTableProps = {
  data: PositionRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  onEditMapping: (position: PositionRow) => void;
  onViewHistory: (position: PositionRow) => void;
  onViewJD?: (position: PositionRow) => void;
};

export function JDMappingTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  onEditMapping,
  onViewHistory,
  onViewJD,
}: JDMappingTableProps) {
  const { has } = usePermissions();
  const cancelMapping = useCancelMapping();
  const columns: TanstackReactTable.ColumnDef<PositionRow>[] = [
    {
      id: "title",
      header: "ຊື່ຕໍາແໜ່ງ",
      cell: ({ row }) => (
        <span className="font-medium">{row.original.title}</span>
      ),
      size: 200,
    },
    {
      id: "departmentName",
      header: "ພະແນກ",
      cell: ({ row }) => (
        <span className="text-sm">{row.original.departmentName || "-"}</span>
      ),
      size: 150,
    },
    {
      id: "level",
      header: "ລະດັບ",
      cell: ({ row }) => <span className="text-sm">{row.original.level}</span>,
      size: 80,
    },
    {
      id: "employeeCount",
      header: "ຈຳນວນຄົນ",
      cell: ({ row }) => (
        <span className="text-sm">{row.original.employeeCount || 0}</span>
      ),
      size: 100,
    },
    {
      id: "currentJD",
      header: "JD ປັດຈຸບັນ",
      cell: ({ row }) => {
        const mapping = row.original.currentMapping;
        if (!mapping) {
          return (
            <Badge variant="secondary" className="text-xs">
              ບໍ່ມີ JD
            </Badge>
          );
        }
        return (
          <div className="space-y-1">
            {onViewJD ? (
              <button
                type="button"
                onClick={() => onViewJD(row.original)}
                className="font-medium text-primary text-sm hover:underline"
              >
                {mapping.jdTitle || "-"}
              </button>
            ) : (
              <span className="font-medium text-sm">
                {mapping.jdTitle || "-"}
              </span>
            )}
            <div className="flex items-center gap-2">
              <Badge
                variant={mapping.status === "active" ? "default" : "secondary"}
                className="text-xs"
              >
                {mapping.status === "active" ? "ໃຊ້ງານ" : "ບໍ່ໃຊ້ງານ"}
              </Badge>
              {mapping.startDate && (
                <span className="text-muted-foreground text-xs">
                  ເລີ່ມ: {formatDateLocal(mapping.startDate)}
                </span>
              )}
            </div>
          </div>
        );
      },
      size: 250,
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => (
        <Badge variant={row.original.status ? "default" : "destructive"}>
          {row.original.status ? "ໃຊ້ງານ" : "ບໍ່ໃຊ້ງານ"}
        </Badge>
      ),
      size: 100,
    },
    {
      id: "actions",
      header: "ການດຳເນີນການ",
      cell: ({ row }: { row: { original: PositionRow } }) => {
        const position = row.original;
        const actions: {
          label: string;
          icon?: React.ReactNode;
          variant?: "destructive";
          onClick: () => void;
        }[] = [];

        if (has("jd_mapping:read") && position.currentMapping && onViewJD) {
          actions.push({
            label: "ເບິ່ງລາຍລະອຽດ",
            icon: <EyeIcon className="h-4 w-4" />,
            onClick: () => onViewJD(position),
          });
        }

        if (has("jd_mapping:create") || has("jd_mapping:update")) {
          actions.push({
            label: position.currentMapping ? "ປ່ຽນ JD" : "ຜູກ JD",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEditMapping(position),
          });
        }

        if (has("jd_mapping:read")) {
          actions.push({
            label: "ປະຫວັດ",
            icon: <HistoryIcon className="h-4 w-4" />,
            onClick: () => onViewHistory(position),
          });
        }

        if (has("jd_mapping:update") && position.currentMapping) {
          actions.push({
            label: "ຍົກເລີກ",
            variant: "destructive",
            icon: <XIcon className="h-4 w-4" />,
            onClick: async () => {
              const confirmed = await confirm({
                title: "ຍົກເລີກການຜູກ JD",
                description: `ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການຍົກເລີກການຜູກ JD "${position.currentMapping.jdTitle}" ກັບຕໍາແໜ່ງ "${position.title}"? ການກະທຳນີ້ອາດຈະສົ່ງຜົນກະທົບຕໍ່ KPI ແລະ ການປະເມີນຜົນງານ.`,
                actionText: "ຍົກເລີກ",
                ActionProps: {
                  variant: "destructive",
                },
              });
              if (confirmed) {
                toast.promise(
                  cancelMapping.mutateAsync({
                    positionId: position.id,
                    notes: "ຍົກເລີກການຜູກ JD",
                  }),
                  {
                    loading: "ກຳລັງຍົກເລີກການຜູກ JD...",
                    success: "ຍົກເລີກການຜູກ JD ສຳເລັດ",
                    error: (err: Error) => err.message || "ຍົກເລີກການຜູກ JD ລົ້ມເຫຼວ",
                  },
                );
              }
            },
          });
        }

        return <RowActions actions={actions} maxInline={2} />;
      },
      size: 200,
    },
  ];

  return (
    <DataTable<PositionRow, unknown>
      noDataMessage="ບໍ່ພົບຕໍາແໜ່ງ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
    />
  );
}
